import React, { useRef } from "react";
import { Stack, Title, Button, Group, Text, Tooltip } from "@mantine/core";
import { IconUpload } from "@tabler/icons-react";
import { notifyError, notifySuccess, notifyWarning } from "../../utils/notify";
import ClientSelectionTable from "./ClientSelectionTable";
import ClientGroupsMultiSelect from "./ClientGroupsMultiSelect";
import { useCampaign } from "../../context/CampaignContext";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import { parseCSV, normalizePhoneDigits } from "../../utils/csvUtils";

function ClientSelectionStep({ onBack, onNext }) {
  const {
    selectedClientIds,
    setSelectedClientIds,
    selectedClientGroupIds,
    setSelectedClientGroupIds,
    clientsLoading,
    allClients,
  } = useCampaign();
  const { hasFeature } = useFeatureAccess();
  const groupsEnabled = hasFeature("client_groups");
  const hasRecipients =
    selectedClientIds.length > 0 || selectedClientGroupIds.length > 0;
  const fileInputRef = useRef(null);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      notifyError("Please upload a .csv file exported from a campaign.", {
        title: "Invalid file",
      });
      return;
    }

    try {
      const text = await file.text();
      const { headers, records } = parseCSV(text);

      if (records.length === 0) {
        notifyWarning("The selected CSV does not contain any rows.", {
          title: "Empty file",
        });
        return;
      }

      const headerSet = new Set(headers);
      const hasClientId = headerSet.has("Client ID");
      const hasPhone =
        headerSet.has("Phone") ||
        headerSet.has("Phone Number") ||
        headerSet.has("Area Code");
      const hasName = headerSet.has("Client Name");

      if (!hasClientId && !hasPhone && !hasName) {
        notifyError(
          "Expected a campaign export with Client ID, Phone, or Client Name columns.",
          { title: "Unrecognized CSV" },
        );
        return;
      }

      const phoneIndex = new Map();
      const nameIndex = new Map();
      const idSet = new Set();
      allClients.forEach((client) => {
        idSet.add(String(client.id));
        const digits = normalizePhoneDigits(
          `${client.area_code || ""}${client.phone_number || ""}`,
        );
        if (digits) phoneIndex.set(digits, client.id);
        const fullName = `${client.first_name || ""} ${client.last_name || ""}`
          .trim()
          .toLowerCase();
        if (fullName) {
          if (!nameIndex.has(fullName)) nameIndex.set(fullName, []);
          nameIndex.get(fullName).push(client.id);
        }
      });

      const matched = new Set();
      const unmatchedRows = [];
      const ambiguousRows = [];

      records.forEach((row, idx) => {
        const csvId = row["Client ID"];
        if (csvId && idSet.has(String(csvId))) {
          matched.add(String(csvId));
          return;
        }

        const phoneSource =
          row["Phone"] ||
          `${row["Area Code"] || ""}${row["Phone Number"] || ""}`;
        const digits = normalizePhoneDigits(phoneSource);
        if (digits && phoneIndex.has(digits)) {
          matched.add(String(phoneIndex.get(digits)));
          return;
        }

        const name = (row["Client Name"] || "").trim().toLowerCase();
        if (name && nameIndex.has(name)) {
          const ids = nameIndex.get(name);
          if (ids.length === 1) {
            matched.add(String(ids[0]));
            return;
          }
          ambiguousRows.push(row["Client Name"] || `Row ${idx + 2}`);
          return;
        }

        unmatchedRows.push(
          row["Client Name"] || row["Phone"] || `Row ${idx + 2}`,
        );
      });

      if (matched.size === 0) {
        notifyError(
          "None of the recipients in the file matched clients in this business.",
          { title: "No clients matched" },
        );
        return;
      }

      const merged = [
        ...new Set([...selectedClientIds.map((id) => String(id)), ...matched]),
      ];
      // Preserve original id type where possible by mapping back through allClients
      const idLookup = new Map(allClients.map((c) => [String(c.id), c.id]));
      setSelectedClientIds(merged.map((id) => idLookup.get(id) ?? id));

      const skipped = unmatchedRows.length + ambiguousRows.length;
      notifySuccess(
        `Selected ${matched.size} client${matched.size === 1 ? "" : "s"} from the CSV` +
          (skipped > 0
            ? `. ${skipped} row${skipped === 1 ? "" : "s"} could not be matched.`
            : "."),
        { title: "Recipients imported" },
      );
    } catch (err) {
      notifyError(err.message || "Could not read the selected file.", {
        title: "Failed to import CSV",
      });
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={3}>Select Clients</Title>
        <Tooltip
          label="Upload a CSV exported from a previous campaign to re-target the same clients"
          withArrow
        >
          <Button
            color="var(--brand-teal)"
            leftSection={<IconUpload size={16} />}
            onClick={handleImportClick}
            disabled={clientsLoading || allClients.length === 0}
          >
            Import from CSV
          </Button>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileSelected}
          style={{ display: "none" }}
        />
      </Group>
      {groupsEnabled && (
        <ClientGroupsMultiSelect
          value={selectedClientGroupIds}
          onChange={setSelectedClientGroupIds}
        />
      )}
      <ClientSelectionTable loading={clientsLoading} />
      {!hasRecipients && (
        <Text color="red" size="sm">
          {groupsEnabled
            ? "Select at least one client or client group."
            : "Select at least one client."}
        </Text>
      )}
      <Group justify="space-between" mt="md">
        <Button variant="default" onClick={onBack}>
          &larr; Back
        </Button>
        <Button onClick={onNext} disabled={!hasRecipients}>
          Next
        </Button>
      </Group>
    </Stack>
  );
}

export default ClientSelectionStep;
